import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, } from '@mui/material';
import { useInterval } from 'app/services';
import { devicesLoad, statusLoad } from 'app/store/retransmission-status';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getApiErrorMessage } from './prefixNameRules';
const statusDotColors = {
    green: 'success.main',
    yellow: 'warning.main',
    red: 'error.main',
    unknown: 'grey.500',
};
const scopeLabels = {
    group: 'групповой',
    transport: 'индивидуальный',
    global: 'глобальный',
};
const headCellSx = {
    fontWeight: 600,
    color: 'text.secondary',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    px: 0.75,
    py: 1,
};
const bodyCellSx = {
    verticalAlign: 'middle',
    px: 0.75,
    py: 1,
};
const deviceErrorCellSx = Object.assign(Object.assign({}, bodyCellSx), { maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
// Бэкенд шлёт ms (Date.now); значения < 1e12 — секунды (Date.now()/1000).
const MS_TIMESTAMP_CUTOFF = 1e12;
function formatTs(value, format = 'DD.MM.YYYY HH:mm:ss') {
    if (!value) {
        return '—';
    }
    return moment(value < MS_TIMESTAMP_CUTOFF ? value * 1000 : value).format(format);
}
function channelName(channel) {
    var _a;
    return (_a = channel.prefix) !== null && _a !== void 0 ? _a : channel.id;
}
function findGroupName(groupItems, groupId) {
    if (groupId === null || groupId === undefined || groupId === '') {
        return null;
    }
    const group = (groupItems || []).find((item) => String(item.id) === String(groupId));
    return group ? group.name || null : null;
}
function RetransmissionStatus({ groupItems }) {
    const dispatch = useDispatch();
    const { status, isLoading, error, devices, devicesLoading, devicesError } = useSelector((state) => state.retransmissionStatus);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const channels = (status === null || status === void 0 ? void 0 : status.channels) || [];
    const isForbidden = (error === null || error === void 0 ? void 0 : error.status) === 403;
    const isOffline = Boolean(status) && !(status === null || status === void 0 ? void 0 : status.online);
    useEffect(() => {
        dispatch(statusLoad());
    }, [dispatch]);
    // 403 — роль не даёт прав, опрос не продолжаем.
    useInterval(() => {
        if (!isForbidden) {
            dispatch(statusLoad());
        }
    }, 12000);
    const handleChannelClick = (channel) => {
        setSelectedChannel(channel);
        dispatch(devicesLoad(channel.id));
    };
    const handleCloseDialog = () => {
        setSelectedChannel(null);
    };
    let indicator = null;
    if (isForbidden) {
        indicator = React.createElement(Alert, { severity: 'info' }, "\u041D\u0435\u0442 \u0434\u043E\u0441\u0442\u0443\u043F\u0430");
    }
    else if (error) {
        indicator = React.createElement(Alert, { severity: 'error' }, getApiErrorMessage(error, 'Не удалось загрузить статус ретрансляции'));
    }
    else if (isOffline) {
        indicator = React.createElement(Alert, { severity: 'warning' }, "retransmit offline");
    }
    else if (status) {
        indicator = (React.createElement(Typography, { variant: 'body2', color: 'text.secondary' },
            "\u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u043E: ",
            formatTs(status.updated_at)));
    }
    else if (isLoading) {
        indicator = React.createElement(CircularProgress, { size: 20 });
    }
    // При offline (нет heartbeat) таблицу не скрываем: каналы показываем как unknown.
    let content = null;
    if (status && !isForbidden) {
        if (channels.length === 0) {
            content = (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' } },
                React.createElement(Typography, { variant: 'body2', color: 'text.secondary' }, "\u041D\u0435\u0442 \u0434\u0430\u043D\u043D\u044B\u0445 \u043F\u043E \u043A\u0430\u043D\u0430\u043B\u0430\u043C")));
        }
        else {
            content = (React.createElement(TableContainer, { sx: { maxHeight: 'calc(100vh - 300px)', overflow: 'auto' } },
                React.createElement(Table, { stickyHeader: true, size: 'small' },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 44, textAlign: 'center' }) }, "\u0421\u0442\u0430\u0442\u0443\u0441"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: '24%' }) }, "\u041A\u0430\u043D\u0430\u043B"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 90 }) }, "\u0422\u0438\u043F"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 132 }) }, "\u041E\u0431\u043B\u0430\u0441\u0442\u044C"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 72, textAlign: 'right' }) }, "OK%"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 72, textAlign: 'right' }) }, "OK"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 88, textAlign: 'right' }) }, "Fail"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 150 }) }, "\u041F\u043E\u0441\u043B\u0435\u0434\u043D\u0438\u0439 fail"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { width: 150 }) }, "\u041E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u043E"))),
                    React.createElement(TableBody, null, channels.map((channel) => {
                        var _a, _b, _c;
                        const name = channelName(channel);
                        // Для группового канала: имя группы из groupItems, иначе fallback на group_id.
                        const groupName = channel.scope === 'group'
                            ? ((_a = findGroupName(groupItems, channel.group_id)) !== null && _a !== void 0 ? _a : (channel.group_id != null && channel.group_id !== '' ? String(channel.group_id) : null))
                            : null;
                        const dotColor = isOffline ? statusDotColors.unknown : statusDotColors[channel.status] || 'grey.500';
                        return (React.createElement(TableRow, { key: channel.id, hover: true, onClick: () => handleChannelClick(channel), sx: { cursor: 'pointer' } },
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, bodyCellSx), { textAlign: 'center' }) },
                                React.createElement(Box, { component: 'span', sx: {
                                        display: 'inline-block',
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: dotColor,
                                    } })),
                            React.createElement(TableCell, { sx: bodyCellSx },
                                React.createElement(Typography, { variant: 'body2', sx: { fontWeight: 600 } }, name),
                                groupName && (React.createElement(Typography, { variant: 'caption', color: 'text.secondary' }, groupName))),
                            React.createElement(TableCell, { sx: bodyCellSx }, channel.kind),
                            React.createElement(TableCell, { sx: bodyCellSx }, scopeLabels[channel.scope] || channel.scope),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, bodyCellSx), { textAlign: 'right' }) },
                                Number((_b = channel.ok_pct) !== null && _b !== void 0 ? _b : 0).toFixed(1),
                                "%"),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, bodyCellSx), { textAlign: 'right' }) }, channel.ok),
                            React.createElement(TableCell, { sx: Object.assign(Object.assign({}, bodyCellSx), { textAlign: 'right' }) },
                                React.createElement(Box, { component: 'span', sx: { display: 'inline-flex', alignItems: 'center', gap: 0.5 } },
                                    channel.fail,
                                    channel.fail_spike && (React.createElement(Tooltip, { title: '\u0432\u0441\u043F\u043B\u0435\u0441\u043A fail' },
                                        React.createElement(WarningAmberRounded, { color: 'warning', fontSize: 'small' }))))),
                            React.createElement(TableCell, { sx: bodyCellSx }, formatTs(channel.last_fail_at)),
                            React.createElement(TableCell, { sx: bodyCellSx }, formatTs((_c = channel.updated_at) !== null && _c !== void 0 ? _c : status.updated_at))));
                    })))));
        }
    }
    const selectedName = selectedChannel ? channelName(selectedChannel) : '';
    const dialogDevices = selectedChannel && devices && devices.channel === selectedChannel.id ? devices.devices : [];
    const sortedDevices = [...dialogDevices].sort((a, b) => (b.fails || 0) - (a.fails || 0));
    let dialogBody = null;
    if (devicesLoading) {
        dialogBody = (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 } },
            React.createElement(CircularProgress, { size: 24 })));
    }
    else if (devicesError) {
        dialogBody = (React.createElement(Alert, { severity: devicesError.status === 403 ? 'info' : 'error' }, getApiErrorMessage(devicesError, 'Не удалось загрузить устройства')));
    }
    else if (sortedDevices.length === 0) {
        dialogBody = (React.createElement(Typography, { variant: 'body2', color: 'text.secondary' }, "\u041D\u0435\u0442 \u0434\u0430\u043D\u043D\u044B\u0445"));
    }
    else {
        dialogBody = (React.createElement(TableContainer, null,
            React.createElement(Table, { size: 'small' },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { sx: headCellSx }, "\u0422\u0421"),
                        React.createElement(TableCell, { sx: Object.assign(Object.assign({}, headCellSx), { textAlign: 'right' }) }, "\u041E\u0448\u0438\u0431\u043E\u043A"),
                        React.createElement(TableCell, { sx: headCellSx }, "\u041F\u043E\u0441\u043B\u0435\u0434\u043D\u044F\u044F \u043E\u0448\u0438\u0431\u043A\u0430"),
                        React.createElement(TableCell, { sx: headCellSx }, "\u0412\u0440\u0435\u043C\u044F fail"),
                        React.createElement(TableCell, { sx: headCellSx }, "\u041A\u0430\u043D\u0430\u043B"))),
                React.createElement(TableBody, null, sortedDevices.map((device) => (React.createElement(TableRow, { key: device.id },
                    React.createElement(TableCell, { sx: bodyCellSx }, device.id),
                    React.createElement(TableCell, { sx: Object.assign(Object.assign({}, bodyCellSx), { textAlign: 'right' }) }, device.fails),
                    React.createElement(TableCell, { sx: deviceErrorCellSx, title: device.last_error || '' }, device.last_error || '—'),
                    React.createElement(TableCell, { sx: bodyCellSx }, formatTs(device.last_fail_at)),
                    React.createElement(TableCell, { sx: bodyCellSx }, device.last_channel || '—'))))))));
    }
    return (React.createElement(Stack, { direction: 'column', sx: { width: '100%', height: '100%', minHeight: 0 } },
        React.createElement(Stack, { direction: 'row', alignItems: 'center', spacing: 1, sx: { width: '100%', mb: 2 } }, indicator),
        content,
        React.createElement(Dialog, { open: Boolean(selectedChannel), onClose: handleCloseDialog, maxWidth: 'sm', fullWidth: true },
            React.createElement(DialogTitle, null,
                "\u041E\u0448\u0438\u0431\u043A\u0438 \u043A\u0430\u043D\u0430\u043B\u0430 \u00AB",
                selectedName,
                "\u00BB"),
            React.createElement(DialogContent, { dividers: true }, dialogBody))));
}
export default RetransmissionStatus;
